import React, { useState, useEffect } from 'react';
import SetupPage from './SetupPage';
import UpdatePage from './UpdatePage';
import VersionPage from './VersionPage';
import PushPage from './PushPage';
import PushSendPage from './PushSendPage';

const C = {
  blue:     '#1a56db',
  blueDark: '#1446b8',
  white:    '#ffffff',
  bg:       '#f0f2f5',
  gray2:    '#f3f4f6',
  gray3:    '#e5e7eb',
  gray4:    '#9ca3af',
  gray5:    '#6b7280',
  gray6:    '#374151',
  gray7:    '#111827',
  cardBorder: '#1a56db',
};

const font = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

// ── Time helpers ─────────────────────────────────────────────────
function relativeDate(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.round((target - now) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0) return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
}

function sentAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr);
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30)  return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

// ── Category icons ───────────────────────────────────────────────
function RoadIcon()  {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
}
function PowerIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4.09 12.96A1 1 0 005 14.5h6.5L10 22l9.91-10.96A1 1 0 0019 9.5H12.5L13 2z"/></svg>;
}
function WaterIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C12 2 5 10 5 14.5a7 7 0 0014 0C19 10 12 2 12 2z"/></svg>;
}
function InfoIcon()  {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>;
}
function CategoryIcon({ category }) {
  const c = (category || '').toLowerCase();
  if (c.includes('road') || c.includes('traffic') || c.includes('closure')) return <RoadIcon />;
  if (c.includes('power') || c.includes('electric')) return <PowerIcon />;
  if (c.includes('water')) return <WaterIcon />;
  return <InfoIcon />;
}

// ── Parse message ────────────────────────────────────────────────
function parseBody(raw) {
  try { return JSON.parse(raw); }
  catch { return { title: raw, summary: '', bullets: [], impact: '', category: '', workDate: null, cta: null }; }
}

// ── Plus button ──────────────────────────────────────────────────
function PlusBtn({ onClick, open }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 28, height: 28, borderRadius: 6,
        background: open ? C.blue : C.blue,
        border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        {open
          ? <path d="M2 7h10" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          : <><path d="M7 2v10" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M2 7h10" stroke="white" strokeWidth="2" strokeLinecap="round"/></>
        }
      </svg>
    </button>
  );
}

// ── Message Card ─────────────────────────────────────────────────
function MessageCard({ message, isLatest }) {
  const [expanded, setExpanded] = useState(false);
  const data = parseBody(message.body);
  const workRel = relativeDate(data.workDate);
  const sentStr = sentAgo(message.sent_at);

  // Header label
  const headerLabel = (() => {
    if (!isLatest) return sentStr;
    if (!data.workDate) return 'Latest message';
    const diff = Math.round((new Date(data.workDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff > 0) return 'Coming up';
    if (diff === 0) return 'Today';
    return 'Recent alert';
  })();

  return (
    <div style={{
      background: C.white,
      borderRadius: 12,
      border: `2px solid ${isLatest ? C.blue : C.gray3}`,
      marginBottom: 10,
      overflow: 'hidden',
    }}>
      {/* Blue header */}
      <div style={{
        background: isLatest ? C.blue : '#6b7280',
        color: C.white,
        padding: '9px 14px',
        fontSize: 13, fontWeight: 600,
        textAlign: 'center',
      }}>
        {headerLabel}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>

        {/* Category + date */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.gray5 }}>
            <CategoryIcon category={data.category} />
            {data.category || 'Message'}
          </div>
          {workRel && (
            <div style={{ fontSize: 12, color: C.gray5 }}>{workRel}</div>
          )}
        </div>

        {/* Title */}
        <div style={{ fontSize: 20, fontWeight: 700, color: C.gray7, lineHeight: 1.2, marginBottom: 3 }}>
          {data.title}
        </div>

        {/* Summary */}
        {data.summary && (
          <div style={{ fontSize: 13, color: C.gray5, marginBottom: 8 }}>
            {data.summary}
          </div>
        )}

        {/* Bullets */}
        {data.bullets && data.bullets.length > 0 && (
          <ul style={{ margin: '0 0 8px 0', padding: '0 0 0 16px', fontSize: 13, color: C.gray6, lineHeight: 1.9 }}>
            {data.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}

        {/* Expand section */}
        {data.impact && (
          <>
            <div style={{ borderTop: `1px solid ${C.gray3}`, marginTop: 6, paddingTop: 8 }}>
              <button
                onClick={() => setExpanded(!expanded)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: 0, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontFamily: font,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: C.blue }}>
                  How does it affect me?
                </span>
                <PlusBtn open={expanded} />
              </button>
            </div>

            {expanded && (
              <div style={{ marginTop: 10, fontSize: 13, color: C.gray6, lineHeight: 1.6 }}>
                {data.impact}

                {data.cta && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.gray6, marginBottom: 4 }}>Need assistance?</div>
                    <a
                      href={data.cta.type === 'phone' ? `tel:${data.cta.value}` : data.cta.value}
                      style={{ fontSize: 13, color: C.blue, textDecoration: 'underline' }}
                    >
                      {data.cta.label || data.cta.value}
                    </a>
                  </div>
                )}

                <div style={{ fontSize: 11, color: C.gray4, marginTop: 12 }}>
                  Sent {sentStr}
                </div>
              </div>
            )}
          </>
        )}

        {/* No expand — show sent */}
        {!data.impact && (
          <div style={{ fontSize: 11, color: C.gray4, marginTop: 4 }}>Sent {sentStr}</div>
        )}
      </div>
    </div>
  );
}

// ── Wifi arc logo ─────────────────────────────────────────────────
function Logo({ postcode }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 20, paddingBottom: 8 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.gray7, letterSpacing: '-0.02em', fontFamily: font }}>
        Message Beacon
      </div>
      {postcode && (
        <div style={{ fontSize: 13, color: C.gray4, marginTop: 2 }}>{postcode}</div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────
export default function App() {
  const [latest,   setLatest]   = useState(null);
  const [history,  setHistory]  = useState([]);
  const [showAll,  setShowAll]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [postcode, setPostcode] = useState('');

  // Setup page route
  if (window.location.pathname === '/setup') return <SetupPage />;
  if (window.location.pathname === '/update') return <UpdatePage />;
  if (window.location.pathname === '/version') return <VersionPage />;
  if (window.location.pathname === '/push') return <PushPage />;
  if (window.location.pathname === '/push/send') return <PushSendPage />;

  const id   = new URLSearchParams(window.location.search).get('id');
  const stopBeacon = new URLSearchParams(window.location.search).get('stop') !== 'false';

  // Log visit (only for real user visits, not admin preview)
  useEffect(() => {
    if (!id) return;
    // Don't log admin previews (?stop=false)
    if (new URLSearchParams(window.location.search).get('stop') === 'false') return;
    const logVisit = async (messageId, wasFirstOpen) => {
      try {
        await fetch('/api/visits', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            beacon_id: atob(id),
            message_id: messageId || null,
            was_first_open: wasFirstOpen,
            language: navigator.language,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            referrer: document.referrer || null,
          }),
        });
      } catch {}
    };
    // Store logVisit for use after message fetch
    window._logVisit = logVisit;
  }, [id, stopBeacon]);

  useEffect(() => {
    if (!id) { setLoading(false); setError('no-id'); return; }
    fetch(`/api/messages/for-user?id=${encodeURIComponent(id)}&stop=${stopBeacon}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); }
        else {
          setLatest(data);
          // Log visit
          if (window._logVisit) window._logVisit(data.id, stopBeacon && !data.displayed_at);
          // Extract postcode from address if available
          try {
            const addr = data.user_address || '';
            const pc = addr.split(',').pop().trim();
            if (pc) setPostcode(pc.toUpperCase());
          } catch {}
        }
        setLoading(false);
      })
      .catch(() => { setError('network'); setLoading(false); });
  }, [id]);

  function loadHistory() {
    fetch(`/api/messages/history-public?id=${encodeURIComponent(id)}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) { setHistory(data); setShowAll(true); } })
      .catch(() => {});
  }

  const wrap = {
    minHeight: '100vh',
    background: C.bg,
    fontFamily: font,
    paddingBottom: 60,
    width: '100%',
    overflowX: 'hidden',
  };

  const inner = {
    width: '100%',
    maxWidth: 440,
    margin: '0 auto',
    padding: '0 14px',
    boxSizing: 'border-box',
  };

  // Landing page (no id)
  if (!id || error === 'no-id') return (
    <div style={{ ...wrap, background: `linear-gradient(160deg, ${C.blue}, ${C.blueDark})` }}>
      <div style={{ ...inner, marginTop: 80, textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: C.white, lineHeight: 1.3, marginBottom: 20 }}>
          MessageBeacon is a prototype system to allow Network Providers to communicate with customers about planned works or issues.
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
          For more information contact:{' '}
          <a href="mailto:someone@somewhere.co" style={{ color: C.white, fontWeight: 500 }}>
            someone@somewhere.co
          </a>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={wrap}>
      <Logo postcode="" />
      <div style={{ ...inner, textAlign: 'center', color: C.gray4, fontSize: 13, paddingTop: 40 }}>Loading…</div>
    </div>
  );

  if (error === 'No messages found') return (
    <div style={wrap}>
      <Logo postcode={postcode} />
      <div style={inner}>
        <div style={{ background: C.white, borderRadius: 12, border: `2px solid ${C.gray3}`, padding: '24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.gray7, marginBottom: 6 }}>No messages</div>
          <div style={{ fontSize: 13, color: C.gray5 }}>You don't have any messages yet.</div>
        </div>
      </div>
    </div>
  );

  const olderMessages = history.filter(m => m.id !== latest?.id);

  return (
    <div style={wrap}>
      <Logo postcode={postcode} />

      <div style={inner}>
        {/* Latest message */}
        {latest && <MessageCard message={latest} isLatest={true} />}

        {/* Past alerts button */}
        <button
          onClick={showAll ? () => setShowAll(false) : loadHistory}
          style={{
            width: '100%',
            background: showAll ? C.gray6 : '#6b7280',
            color: C.white,
            border: 'none',
            borderRadius: 10,
            padding: '12px 14px',
            fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', fontFamily: font,
            marginBottom: showAll ? 10 : 0,
          }}
        >
          Past alerts
          <PlusBtn open={showAll} />
        </button>

        {/* History */}
        {showAll && olderMessages.length > 0 && (
          <div style={{ marginTop: 2 }}>
            {olderMessages.map(m => <MessageCard key={m.id} message={m} isLatest={false} />)}
          </div>
        )}

        {showAll && olderMessages.length === 0 && (
          <div style={{ textAlign: 'center', fontSize: 12, color: C.gray4, padding: '16px 0' }}>
            No older messages
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(8px)',
        borderTop: `1px solid ${C.gray3}`,
        padding: '10px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontSize: 12, color: C.gray4,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01" strokeLinecap="round"/>
        </svg>
        Tap beacon anytime to read alerts
      </div>
    </div>
  );
}
